import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';
import { mockAnuncios, mockPlanos, mockUsuario, mockConversas } from './mockData';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true' || !import.meta.env.VITE_API_URL;

// Mock API responses
const mockResponses: Record<string, any> = {
  'GET /anuncios': { data: { anuncios: mockAnuncios, total: mockAnuncios.length, paginas: 1 } },
  'GET /anuncios/destaques': { data: mockAnuncios.slice(0, 4) },
  'GET /anuncios/recentes': { data: mockAnuncios.slice(0, 6) },
  'GET /planos': { data: mockPlanos },
  'GET /auth/me': { data: mockUsuario },
  'GET /conversas': { data: mockConversas },
  'POST /auth/login': { data: { token: 'mock-token', usuario: mockUsuario } },
  'POST /auth/register': { data: { token: 'mock-token', usuario: mockUsuario } },
};

// Create mock handler
const mockHandler = {
  request: async (config: any) => {
    const method = config.method?.toUpperCase() || 'GET';
    const url = config.url?.replace(API_URL, '') || '';
    const key = `${method} ${url}`.split('?')[0];
    
    // Check for specific anuncio
    if (url.match(/^\/anuncios\/[^/]+$/) && method === 'GET') {
      const id = url.split('/').pop();
      const anuncio = mockAnuncios.find(a => a.id === id);
      if (anuncio) return { data: anuncio };
    }
    
    if (mockResponses[key]) {
      return mockResponses[key];
    }
    
    // Default response for unknown endpoints
    return { data: { message: 'Mock response' } };
  }
};

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // If using mock, intercept here
    if (USE_MOCK) {
      const mockResponse = await mockHandler.request(config);
      return Promise.reject({ 
        __MOCK__: true, 
        response: { data: mockResponse.data, status: 200 } 
      });
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error: any) => {
    // Handle mock responses
    if (error.__MOCK__) {
      return error.response;
    }
    
    const axiosError = error as AxiosError<{ message?: string; error?: string }>;
    const message = axiosError.response?.data?.message || 
                   axiosError.response?.data?.error || 
                   'Erro ao processar requisição';

    switch (axiosError.response?.status) {
      case 401:
        localStorage.removeItem('token');
        localStorage.removeItem('usuario');
        if (window.location.pathname !== '/login') {
          toast.error('Sessão expirada. Faça login novamente.');
          window.location.href = '/login';
        }
        break;
      case 403:
        toast.error('Você não tem permissão para esta ação.');
        break;
      case 404:
        toast.error('Recurso não encontrado.');
        break;
      case 500:
        toast.error('Erro interno do servidor.');
        break;
      default:
        if (!axiosError.response) {
          toast.error('Erro de conexão.');
        }
    }

    return Promise.reject(error);
  }
);

export const apiMultipart = axios.create({
  baseURL: API_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'multipart/form-data',
  },
});

apiMultipart.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export default api;
